/**
 * Manager process — single-process manager with PID management and state files.
 * No fork/IPC — the poll loop runs directly in this process.
 */

import { readFileSync, writeFileSync, unlinkSync, existsSync, mkdirSync, openSync, closeSync, chmodSync } from 'node:fs';
import { join } from 'node:path';
import { spawn, execFileSync } from 'node:child_process';
// ENG-9129: `log` comes from the dependency-free manager runtime leaf (node
// stdlib only, ADR-0011), NOT from manager-worker.js. That distinction is the
// entire reason these lines can exist: the window being reported on is the
// import of manager-worker.js itself, so anything reached THROUGH that module
// is unavailable for exactly as long as there is something to report.
import { log } from './manager/runtime.js';
import {
  describeWorkerModuleFailure,
  describeWorkerModuleLoad,
} from './startup-telemetry.js';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface WatchdogOptions {
  intervalMs: number;
  configDir: string;
  /**
   * When true, fork a detached child process to run the manager instead of
   * running it in-process. Required for callers like `agt setup` that need
   * to exit cleanly after emitting output — without detach, the manager's
   * timers and subscriptions keep the parent Node process alive forever,
   * which hangs any caller using `$(agt setup ...)` command substitution.
   */
  detached?: boolean;
}

export interface ManagerStatus {
  pid: number;
  startedAt: string;
  lastPollAt: string | null;
  pollCount: number;
  errorCount: number;
  agents: Array<{
    agentId: string;
    codeName: string;
    status: string;
    charterVersion: string;
    toolsVersion: string;
    lastRefreshAt: string | null;
    lastProvisionAt: string | null;
    lastDriftCheckAt: string | null;
    gatewayPort: number | null;
    gatewayPid: number | null;
    gatewayRunning: boolean;
    acpSessions: Array<{
      sessionId: string;
      agentCommand: string;
      sessionName?: string;
      queueState: string;
      turnCount: number;
      startedAt: string;
    }>;
  }>;
}

// ---------------------------------------------------------------------------
// Paths — all resolved relative to the caller's configDir so a non-default
// dir doesn't split manager PID/state/log across locations.
// ---------------------------------------------------------------------------

/** Default config dir. Exported so command definitions share one source. */
export const DEFAULT_CONFIG_DIR = join(process.env['HOME'] ?? '/tmp', '.augmented');

export function getManagerPaths(configDir: string): { pidFile: string; stateFile: string; logFile: string } {
  return {
    pidFile: join(configDir, 'manager.pid'),
    stateFile: join(configDir, 'manager-state.json'),
    logFile: join(configDir, 'manager.log'),
  };
}

function ensureDir(configDir: string): void {
  if (!existsSync(configDir)) {
    mkdirSync(configDir, { recursive: true });
  }
}

// ---------------------------------------------------------------------------
// PID file management
// ---------------------------------------------------------------------------

function writePidFile(configDir: string, pid: number): void {
  ensureDir(configDir);
  writeFileSync(getManagerPaths(configDir).pidFile, String(pid), { mode: 0o600 });
}

function readPidFile(configDir: string): number | null {
  try {
    const raw = readFileSync(getManagerPaths(configDir).pidFile, 'utf-8').trim();
    const pid = parseInt(raw, 10);
    return isNaN(pid) ? null : pid;
  } catch {
    return null;
  }
}

function removePidFile(configDir: string): void {
  try {
    unlinkSync(getManagerPaths(configDir).pidFile);
  } catch {
    // may not exist
  }
}

function isProcessAlive(pid: number): boolean {
  try {
    process.kill(pid, 0);
    return true;
  } catch {
    return false;
  }
}

/**
 * ENG-4714: scan for other `agt manager start` processes regardless of
 * what manager.pid says. Catches the multi-manager bug we hit on Brad's
 * Mac during the Bob-Telegram debug session: launchd respawned the
 * manager mid-`stop` (pidfile already deleted), the operator's
 * subsequent `nohup agt manager start` saw no pidfile and proceeded,
 * resulting in two managers polling the same agents and writing the
 * same `manager.log` (every line duplicated, channel-credentials cache
 * thrashing, etc.).
 *
 * Returns the PIDs of any matching processes other than the current
 * one. Tolerates absent pgrep / unsupported flags by returning an
 * empty list — additive defence over the pidfile check, never a hard
 * dependency.
 */
export function findOtherManagerPids(
  // Injection seam for tests — defaults to the real pgrep call.
  pgrepImpl: () => string = defaultPgrep,
  selfPid: number = process.pid,
): number[] {
  let out: string;
  try {
    out = pgrepImpl();
  } catch (err) {
    // pgrep absent / errored / no matches (exit 1 is the no-match
    // case). Treat all of these as "no duplicates detected" — the
    // pidfile check is the primary line of defence.
    //
    // ENG-9129: one of those outcomes is not like the others. Exit 1 (no
    // match) is the overwhelmingly common case and must stay silent, or every
    // healthy start logs noise. A KILLED scan is different: `execFileSync`
    // terminates the child on timeout, so `signal` being set is specifically
    // "the process table took longer than PGREP_SCAN_TIMEOUT_MS to read",
    // which is a statement about host load at the exact moment a manager is
    // booting. That is the observation the incident had no way to make.
    const signal = (err as { signal?: NodeJS.Signals | null })?.signal;
    if (signal) {
      log(
        `[startup] duplicate-manager scan timed out after ${PGREP_SCAN_TIMEOUT_MS}ms (killed by ${signal}) — ` +
          'proceeding without it, since the pidfile check is the primary guard. A process-table read this ' +
          'slow means the host is heavily loaded, which is also when a boot is most likely to stall ' +
          '(ENG-9129).',
      );
    }
    return [];
  }
  return out
    .split('\n')
    .map((line) => parseInt(line.trim(), 10))
    .filter((pid) => !isNaN(pid) && pid !== selfPid);
}

/**
 * ENG-9129: bounded, because this is the ONLY unbounded blocking call between
 * process spawn and the manager's first log line.
 *
 * `pgrep -f` walks /proc and reads every process's full command line, so its
 * cost scales with how busy the host is — and the case that matters here is a
 * host mid-provisioning, churning short-lived build and install processes.
 * Every `execFileSync` in manager-worker.ts already passes an explicit
 * `timeout`; this one did not, and it runs earlier than all of them.
 *
 * A timeout makes `execFileSync` throw, and the caller already treats any
 * throw as "no duplicates detected" — the documented, deliberate fallback,
 * since the pidfile check above is the primary defence and this scan is
 * additive. So the failure mode of a slow scan becomes "skip the extra check"
 * rather than "block the manager from starting", which is the right trade in
 * both directions: a doubled manager is recoverable, a manager that never
 * boots is the incident.
 *
 * 10s is generous for a call that is sub-100ms on an idle host; anything
 * beyond it is evidence about the host, not about the process table.
 */
export const PGREP_SCAN_TIMEOUT_MS = 10_000;

function defaultPgrep(): string {
  // -f matches against the full command line. macOS (BSD) and Linux
  // (procps-ng) pgrep both support this flag.
  return execFileSync('pgrep', ['-f', 'agt manager start'], {
    encoding: 'utf-8',
    stdio: ['ignore', 'pipe', 'ignore'],
    timeout: PGREP_SCAN_TIMEOUT_MS,
  });
}

// ---------------------------------------------------------------------------
// State file management
// ---------------------------------------------------------------------------

function readStateFile(configDir: string): ManagerStatus | null {
  try {
    const raw = readFileSync(getManagerPaths(configDir).stateFile, 'utf-8');
    return JSON.parse(raw) as ManagerStatus;
  } catch {
    return null;
  }
}

function removeStateFile(configDir: string): void {
  try {
    unlinkSync(getManagerPaths(configDir).stateFile);
  } catch {
    // may not exist
  }
}

// ---------------------------------------------------------------------------
// Manager (single-process, no fork)
// ---------------------------------------------------------------------------

/**
 * Start the manager. Runs the poll loop directly in this process.
 * This function does not return until the process is stopped.
 */
export function startWatchdog(opts: WatchdogOptions): { pid: number } {
  const { configDir } = opts;
  // Check for existing process — pidfile path
  const existingPid = readPidFile(configDir);
  if (existingPid !== null) {
    if (isProcessAlive(existingPid)) {
      throw new Error(`Manager already running (PID ${existingPid}). Use \`agt manager stop\` first.`);
    }
    // Stale PID file — clean up
    removePidFile(configDir);
    removeStateFile(configDir);
  }

  // ENG-4714: process-scan fallback. Pidfile-only detection misses the
  // race where `agt manager stop` deletes the pidfile while a launchd /
  // systemd respawn is still in flight, then a fresh `agt manager
  // start` from the operator's shell sees no pidfile and proceeds — two
  // managers end up coexisting. The scan fires regardless of pidfile
  // state and refuses to start when another `agt manager start` is
  // already running.
  const others = findOtherManagerPids();
  if (others.length > 0) {
    const pidList = others.join(', ');
    throw new Error(
      `Manager already running (PID ${pidList}). Use \`agt manager stop\` first.`,
    );
  }

  if (opts.detached) {
    // Fork a detached child running `agt manager start`. The child writes its
    // own PID file via the in-process path below; we return its PID so the
    // caller can exit cleanly.
    ensureDir(configDir);
    // Log captures full stdout/stderr including tokens — lock down perms.
    const { logFile } = getManagerPaths(configDir);
    const logFd = openSync(logFile, 'a', 0o600);
    // Normalize perms on an existing file in case it was created with a
    // different umask previously.
    try {
      chmodSync(logFile, 0o600);
    } catch {
      // non-fatal — file might have just been created with 0o600 above
    }
    const intervalSec = String(Math.max(Math.floor(opts.intervalMs / 1000), 5));
    // ENG-4585: launch under the supervisor so an exit-75 from self-update
    // respawns the manager onto the new binary. Without --supervise the
    // detached child exits cleanly, the gateway pool stays stopped, and
    // agents go dark until an operator runs `agt manager start` again.
    const child = spawn(
      process.execPath,
      [process.argv[1]!, 'manager', 'start', '--interval', intervalSec, '--config-dir', configDir, '--supervise'],
      {
        detached: true,
        stdio: ['ignore', logFd, logFd],
        env: process.env,
      },
    );
    // unref so the parent can exit without waiting for the child
    child.unref();
    closeSync(logFd);
    if (!child.pid) {
      throw new Error('Failed to spawn detached manager process');
    }

    // Bounded readiness check: wait for the child to write its PID file, or
    // fail fast if it exits early. Without this, `agt setup --json` would
    // report success even when the manager crashed on startup (e.g. missing
    // AGT_API_KEY), silently producing dead bootstraps.
    const { pidFile } = getManagerPaths(configDir);
    const deadline = Date.now() + 5_000;
    const sleepBuf = new Int32Array(new SharedArrayBuffer(4));
    while (Date.now() < deadline) {
      if (existsSync(pidFile)) {
        return { pid: child.pid };
      }
      if (child.exitCode !== null) {
        throw new Error(
          `Manager exited during startup (code ${child.exitCode}). See ${logFile} for details.`,
        );
      }
      Atomics.wait(sleepBuf, 0, 0, 100);
    }
    throw new Error(
      `Manager did not become ready within 5s. See ${logFile} for details.`,
    );
  }

  // In-process path — the manager timers and subscriptions keep this Node
  // process alive. Used by `agt manager start` where blocking is intentional.
  writePidFile(configDir, process.pid);

  // ENG-9129 — the 25-minute silence on agt-aws-1 (2026-08-18) happened HERE.
  //
  // Read the three statements in order. The pidfile is written, then the
  // worker module is imported WITHOUT being awaited, then this function
  // returns and the caller prints "✔ Manager started (PID …)". So by the time
  // the operator is told the manager started, the manager has not been loaded,
  // let alone run. Everything the manager does — the poll loop, the first log
  // line, deployMcpAssets() — is on the far side of this import.
  //
  // The window was silent rather than sparse because `log()` was reached
  // through the very module being imported. Nothing suppressed those lines;
  // there was no code loaded to emit them. Hence the two lines below, which
  // come from the dependency-free runtime leaf instead.
  //
  // `process.uptime()` rather than a captured Date: it is measured from spawn
  // by the runtime, so a slow module load is reported against the true process
  // age even if something before this point was also slow, and there is no
  // second timestamp that can drift.
  const moduleLoadStartedAt = process.uptime() * 1000;
  log('[startup] loading worker module — nothing polls, logs or deploys MCP bundles until this resolves');
  // `void` marks the chain as deliberately un-awaited, exactly as before. What
  // changed is that the chain now has a rejection handler, so "un-awaited" no
  // longer also means "unobserved".
  void import('./manager-worker.js')
    .then(({ startManager }) => {
      const { message } = describeWorkerModuleLoad(process.uptime() * 1000 - moduleLoadStartedAt);
      log(message);
      startManager({
        intervalMs: opts.intervalMs,
        configDir,
      });
    })
    .catch((err: unknown) => {
      // Previously `void import(...)` with no rejection handler. A failed
      // import left a process holding a pidfile, having announced itself
      // started, that would never poll and never exit — and `agt manager
      // status` reads that pidfile, so it reported a healthy manager forever.
      // Exit non-zero so the supervisor sees a failure rather than a hang.
      //
      // The pidfile is cleared by the `process.on('exit')` handler registered
      // just below, which is already in place by the time this async rejection
      // can fire — clearing it here as well would only invite the two to
      // disagree later.
      log(describeWorkerModuleFailure(err));
      process.exit(1);
    });

  // Clean up PID file on exit
  process.on('exit', () => {
    removePidFile(configDir);
  });

  return { pid: process.pid };
}

/**
 * Stop a running manager by reading the PID file and sending SIGTERM.
 */
export async function stopWatchdog(configDir: string = DEFAULT_CONFIG_DIR): Promise<{ stopped: boolean; pid?: number }> {
  const pid = readPidFile(configDir);
  if (pid === null) {
    return { stopped: false };
  }

  if (!isProcessAlive(pid)) {
    // Stale PID — clean up
    removePidFile(configDir);
    removeStateFile(configDir);
    return { stopped: true, pid };
  }

  // Send SIGTERM
  process.kill(pid, 'SIGTERM');

  // Poll for up to 5 seconds until the process exits
  const deadline = Date.now() + 5_000;
  while (Date.now() < deadline) {
    await new Promise((r) => setTimeout(r, 200));
    if (!isProcessAlive(pid)) {
      removePidFile(configDir);
      return { stopped: true, pid };
    }
  }

  // Still alive after 5s — force kill
  try {
    process.kill(pid, 'SIGKILL');
  } catch {
    // may have died between checks
  }
  removePidFile(configDir);
  removeStateFile(configDir);
  return { stopped: true, pid };
}

/**
 * Get the current manager status by reading PID + state files.
 */
export function getManagerStatus(configDir: string = DEFAULT_CONFIG_DIR): ManagerStatus | null {
  const pid = readPidFile(configDir);
  if (pid === null) return null;

  if (!isProcessAlive(pid)) {
    removePidFile(configDir);
    removeStateFile(configDir);
    return null;
  }

  return readStateFile(configDir);
}
